/**
 * Lands-data for frontend.
 *
 * - ISO 3166-1 alpha-2-koder lagres i DB
 * - Navn per språk (no/sv/da/en)
 * - Flagg via emoji (codepoint = 'A' + offset for hver bokstav)
 */

// ISO-kode → flagg-emoji. Bruker Unicode regional indicator symbols.
export function flagEmoji(isoCode) {
  if (!isoCode || typeof isoCode !== "string" || isoCode.length !== 2) return "🌐";
  const code = isoCode.toUpperCase();
  // Unicode 🇦 = 0x1F1E6, 'A'.charCodeAt(0) = 65
  const A = 0x41;
  const base = 0x1F1E6;
  try {
    return String.fromCodePoint(base + (code.charCodeAt(0) - A)) +
           String.fromCodePoint(base + (code.charCodeAt(1) - A));
  } catch {
    return "🌐";
  }
}

// Lokaliserte navn for alle 197 land. Default-fallback til engelsk hvis et språk mangler.
// no/sv/da/en. Tar med alle land vi har i backend.
const NAMES = {
  NO: { no: "Norge",         sv: "Norge",         da: "Norge",        en: "Norway" },
  SE: { no: "Sverige",       sv: "Sverige",       da: "Sverige",      en: "Sweden" },
  DK: { no: "Danmark",       sv: "Danmark",       da: "Danmark",      en: "Denmark" },
  GB: { no: "Storbritannia (UK)", sv: "Storbritannien (UK)", da: "Storbritannien (UK)", en: "United Kingdom" },
  US: { no: "USA",           sv: "USA",           da: "USA",          en: "United States" },
  // Resten – engelsk er fallback. Vi gir norske navn for nabolandsregion.
  FI: { no: "Finland",       sv: "Finland",       da: "Finland",      en: "Finland" },
  IS: { no: "Island",        sv: "Island",        da: "Island",       en: "Iceland" },
  DE: { no: "Tyskland",      sv: "Tyskland",      da: "Tyskland",     en: "Germany" },
  FR: { no: "Frankrike",     sv: "Frankrike",     da: "Frankrig",     en: "France" },
  ES: { no: "Spania",        sv: "Spanien",       da: "Spanien",      en: "Spain" },
  IT: { no: "Italia",        sv: "Italien",       da: "Italien",      en: "Italy" },
  NL: { no: "Nederland",     sv: "Nederländerna", da: "Nederlandene", en: "Netherlands" },
  PL: { no: "Polen",         sv: "Polen",         da: "Polen",        en: "Poland" },
  RU: { no: "Russland",      sv: "Ryssland",      da: "Rusland",      en: "Russia" },
  // Engelsk-fallback for resten
  AD: { en: "Andorra" }, AE: { en: "United Arab Emirates" }, AF: { en: "Afghanistan" },
  AG: { en: "Antigua and Barbuda" }, AL: { en: "Albania" }, AM: { en: "Armenia" },
  AO: { en: "Angola" }, AR: { en: "Argentina" }, AT: { en: "Austria" }, AU: { en: "Australia" }, AZ: { en: "Azerbaijan" },
  BA: { en: "Bosnia and Herzegovina" }, BB: { en: "Barbados" }, BD: { en: "Bangladesh" }, BE: { en: "Belgium" },
  BF: { en: "Burkina Faso" }, BG: { en: "Bulgaria" }, BH: { en: "Bahrain" }, BI: { en: "Burundi" },
  BJ: { en: "Benin" }, BN: { en: "Brunei" }, BO: { en: "Bolivia" }, BR: { en: "Brazil" }, BS: { en: "Bahamas" },
  BT: { en: "Bhutan" }, BW: { en: "Botswana" }, BY: { en: "Belarus" }, BZ: { en: "Belize" },
  CA: { en: "Canada" }, CD: { en: "DR Congo" }, CF: { en: "Central African Republic" }, CG: { en: "Congo" },
  CH: { en: "Switzerland" }, CI: { en: "Côte d'Ivoire" }, CL: { en: "Chile" }, CM: { en: "Cameroon" },
  CN: { en: "China" }, CO: { en: "Colombia" }, CR: { en: "Costa Rica" }, CU: { en: "Cuba" },
  CV: { en: "Cabo Verde" }, CY: { en: "Cyprus" }, CZ: { en: "Czech Republic" },
  DJ: { en: "Djibouti" }, DM: { en: "Dominica" }, DO: { en: "Dominican Republic" }, DZ: { en: "Algeria" },
  EC: { en: "Ecuador" }, EE: { en: "Estonia" }, EG: { en: "Egypt" }, ER: { en: "Eritrea" }, ET: { en: "Ethiopia" },
  FJ: { en: "Fiji" }, FM: { en: "Micronesia" },
  GA: { en: "Gabon" }, GD: { en: "Grenada" }, GE: { en: "Georgia" }, GH: { en: "Ghana" }, GM: { en: "Gambia" },
  GN: { en: "Guinea" }, GQ: { en: "Equatorial Guinea" }, GR: { en: "Greece" }, GT: { en: "Guatemala" },
  GW: { en: "Guinea-Bissau" }, GY: { en: "Guyana" },
  HK: { en: "Hong Kong" }, HN: { en: "Honduras" }, HR: { en: "Croatia" }, HT: { en: "Haiti" }, HU: { en: "Hungary" },
  ID: { en: "Indonesia" }, IE: { en: "Ireland" }, IL: { en: "Israel" }, IN: { en: "India" },
  IQ: { en: "Iraq" }, IR: { en: "Iran" },
  JM: { en: "Jamaica" }, JO: { en: "Jordan" }, JP: { en: "Japan" },
  KE: { en: "Kenya" }, KG: { en: "Kyrgyzstan" }, KH: { en: "Cambodia" }, KI: { en: "Kiribati" },
  KM: { en: "Comoros" }, KN: { en: "Saint Kitts and Nevis" }, KP: { en: "North Korea" }, KR: { en: "South Korea" },
  KW: { en: "Kuwait" }, KZ: { en: "Kazakhstan" },
  LA: { en: "Laos" }, LB: { en: "Lebanon" }, LC: { en: "Saint Lucia" }, LI: { en: "Liechtenstein" },
  LK: { en: "Sri Lanka" }, LR: { en: "Liberia" }, LS: { en: "Lesotho" }, LT: { en: "Lithuania" },
  LU: { en: "Luxembourg" }, LV: { en: "Latvia" }, LY: { en: "Libya" },
  MA: { en: "Morocco" }, MC: { en: "Monaco" }, MD: { en: "Moldova" }, ME: { en: "Montenegro" },
  MG: { en: "Madagascar" }, MH: { en: "Marshall Islands" }, MK: { en: "North Macedonia" }, ML: { en: "Mali" },
  MM: { en: "Myanmar" }, MN: { en: "Mongolia" }, MR: { en: "Mauritania" }, MT: { en: "Malta" },
  MU: { en: "Mauritius" }, MV: { en: "Maldives" }, MW: { en: "Malawi" }, MX: { en: "Mexico" },
  MY: { en: "Malaysia" }, MZ: { en: "Mozambique" },
  NA: { en: "Namibia" }, NE: { en: "Niger" }, NG: { en: "Nigeria" }, NI: { en: "Nicaragua" },
  NP: { en: "Nepal" }, NR: { en: "Nauru" }, NZ: { en: "New Zealand" },
  OM: { en: "Oman" },
  PA: { en: "Panama" }, PE: { en: "Peru" }, PG: { en: "Papua New Guinea" }, PH: { en: "Philippines" },
  PK: { en: "Pakistan" }, PT: { en: "Portugal" }, PW: { en: "Palau" }, PY: { en: "Paraguay" },
  QA: { en: "Qatar" },
  RO: { en: "Romania" }, RS: { en: "Serbia" }, RW: { en: "Rwanda" },
  SA: { en: "Saudi Arabia" }, SB: { en: "Solomon Islands" }, SC: { en: "Seychelles" }, SD: { en: "Sudan" },
  SG: { en: "Singapore" }, SI: { en: "Slovenia" }, SK: { en: "Slovakia" }, SL: { en: "Sierra Leone" },
  SM: { en: "San Marino" }, SN: { en: "Senegal" }, SO: { en: "Somalia" }, SR: { en: "Suriname" },
  SS: { en: "South Sudan" }, ST: { en: "São Tomé and Príncipe" }, SV: { en: "El Salvador" },
  SY: { en: "Syria" }, SZ: { en: "Eswatini" },
  TD: { en: "Chad" }, TG: { en: "Togo" }, TH: { en: "Thailand" }, TJ: { en: "Tajikistan" },
  TL: { en: "Timor-Leste" }, TM: { en: "Turkmenistan" }, TN: { en: "Tunisia" }, TO: { en: "Tonga" },
  TR: { en: "Turkey" }, TT: { en: "Trinidad and Tobago" }, TV: { en: "Tuvalu" }, TW: { en: "Taiwan" }, TZ: { en: "Tanzania" },
  UA: { en: "Ukraine" }, UG: { en: "Uganda" }, UY: { en: "Uruguay" }, UZ: { en: "Uzbekistan" },
  VA: { en: "Vatican City" }, VC: { en: "Saint Vincent and the Grenadines" }, VE: { en: "Venezuela" },
  VN: { en: "Vietnam" }, VU: { en: "Vanuatu" },
  WS: { en: "Samoa" },
  XK: { en: "Kosovo" },
  YE: { en: "Yemen" },
  ZA: { en: "South Africa" }, ZM: { en: "Zambia" }, ZW: { en: "Zimbabwe" },
};

/**
 * countryName(iso, lang) → localized name. Faller tilbake til engelsk, så ISO-kode.
 */
export function countryName(iso, lang = "no") {
  if (!iso) return "";
  const entry = NAMES[iso];
  if (!entry) return iso;
  return entry[lang] || entry.en || iso;
}

/**
 * Returnerer en sortert liste over alle land for dropdown.
 * Topp-faste først (i fast rekkefølge), deretter resten alfabetisk på lokalisert navn.
 */
export function buildCountryOptions(allIsoCodes, topIsoCodes, lang = "no") {
  const top = topIsoCodes
    .filter(iso => allIsoCodes.includes(iso))
    .map(iso => ({ iso, name: countryName(iso, lang), flag: flagEmoji(iso), pinned: true }));
  const rest = allIsoCodes
    .filter(iso => !topIsoCodes.includes(iso))
    .map(iso => ({ iso, name: countryName(iso, lang), flag: flagEmoji(iso), pinned: false }))
    .sort((a, b) => a.name.localeCompare(b.name, lang === "no" ? "nb" : lang));
  return { top, rest };
}
